import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Checkbox, Popover } from 'antd';
import { CheckCircleTwoTone } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { collect } from 'collect.js';
import FadeIn from '../FadeIn';
import ConfirmInformationIllustration from '../../resources/assets/illustrations/ConfirmInformationIllustration';
import PendingVerification from './status/PendingVerification';
import CompleteVerification from './status/CompleteVerification';
import useApiClient from '../../hooks/api/useApiClient';
import LoadingIndicator from '../LoadingIndicator';
import { PaymentGateway, PaymentGatewayVerificationStatus } from '../../services/exports/Constants';
import { BusinessProfileUpdateStrategy } from '../../providers/BusinessProfileProvider';
import OnboardingLinkButton, { OnboardingLinkButtonMode } from '../buttons/OnboardingLinkButton';

export default function InfoSubmissionStep({ paymentGateway, moveForward, ...rest }) {
  const { CurrentCompanyManager } = useApiClient();
  const { company } = useSelector((state) => state.currentCompany);

  const status = company?.[`${paymentGateway}_verification_status`];

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    mount();
  }, []);

  const mount = () => {
    setTimeout(
      () =>
        CurrentCompanyManager.get()
          .then((response) => {
            if (!response.success) {
              return null;
            }

            const freshStatus = response.data[`${paymentGateway}_verification_status`];
            const isDone =
              freshStatus === PaymentGatewayVerificationStatus.Pending ||
              freshStatus === PaymentGatewayVerificationStatus.Verified;

            return isDone && moveForward();
          })
          .finally(() => setMounted(true)),
      2000,
    );
  };

  if (status === PaymentGatewayVerificationStatus.Pending) {
    return <PendingVerification />;
  }

  if (status === PaymentGatewayVerificationStatus.Verified) {
    return <CompleteVerification />;
  }

  const Content = {
    [PaymentGateway.Stripe]: StripeContent,
    [PaymentGateway.Adyen]: AdyenContent,
  }[paymentGateway];

  return (
    <FadeIn>
      {!mounted && (
        <div className="my-md">
          <LoadingIndicator />
        </div>
      )}
      {mounted && Content && <Content moveForward={moveForward} {...rest} />}
    </FadeIn>
  );
}

function AdyenContent() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Verification:InfoSubmissionStep' });

  return (
    <div className="flex flex-col lg:flex-row lg:items-center mx-auto">
      <div className="relative lg:w-5/12 text-center max-w-lg mx-auto lg:max-w-none lg:text-left">
        <h5>{t('title')}</h5>
        <p className="mt-md text-gray-default">{t('payment_gateways.adyen.description')}</p>
        <div className="flex gap-2 mt-md">
          <OnboardingLinkButton paymentGateway={PaymentGateway.Adyen} text={t('buttons.proceed')} type="primary" />
          <OnboardingLinkButton
            paymentGateway={PaymentGateway.Adyen}
            text={t('buttons.copy_link')}
            mode={OnboardingLinkButtonMode.Copy}
          />
        </div>
      </div>
      <div className="relative lg:mt-0 flex-1 flex flex-col justify-center items-center lg:items-end lg:p-md lg:self-end order-first lg:order-last">
        <ConfirmInformationIllustration />
      </div>
    </div>
  );
}

function StripeContent({ moveForward }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Verification:InfoSubmissionStep' });

  const { company } = useSelector((state) => state.currentCompany);
  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);

  const { BusinessProfilesManager } = useApiClient();

  const [loading, setLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const onProceed = async () => {
    setLoading(true);
    const { success } = await BusinessProfilesManager.update({
      terms_accepted: termsAccepted,
      update_strategy: BusinessProfileUpdateStrategy.All,
    });
    setLoading(false);

    if (!success) {
      return toast.error(t('toasts.something_went_wrong'));
    }

    return moveForward();
  };

  const tosAccepted = company.business_profile.stripe_tos_acceptance;
  const currentlyDue = company.merchant.stripe_requirements?.currently_due ?? [];

  if (tosAccepted && currentlyDue.length > 0) {
    return (
      <>
        <p className="mb-xs">{t('payment_gateways.stripe.labels.stripe_requirements')}</p>
        {currentlyDue.map((requirement) => (
          <Popover
            key={`requirement-${requirement}`}
            title={t('payment_gateways.stripe.labels.errors')}
            content={
              <ul>
                {collect(company.merchant.stripe_requirements.errors)
                  .where('requirement', requirement)
                  .map((error) => <li key={`requirement-${requirement}-error-${error.code}`}>{error.reason}</li>)
                  .toArray()}
              </ul>
            }
            overlayClassName="lg:max-w-1/2"
          >
            {requirement} <br />
          </Popover>
        ))}
      </>
    );
  }

  if (tosAccepted) {
    return (
      <div className="flex flex-col lg:flex-row lg:items-center mx-auto">
        <div className="relative lg:w-5/12 text-center max-w-lg mx-auto lg:max-w-none lg:text-left">
          <h5 className="inline-flex items-center gap-2">
            <CheckCircleTwoTone twoToneColor="#52c41a" />
            {t('payment_gateways.stripe.labels.awaiting_verification')}
          </h5>
          <p className="mt-md text-gray-default">
            {t('payment_gateways.stripe.labels.awaiting_verification_description')}
          </p>
          <div className="flex gap-2 mt-md">
            <OnboardingLinkButton paymentGateway={PaymentGateway.Stripe} />
          </div>
        </div>
        <div className="relative lg:mt-0 flex-1 flex flex-col justify-center items-center lg:items-end lg:p-md lg:self-end order-first lg:order-last">
          <ConfirmInformationIllustration />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row lg:items-center mx-auto">
      <div className="relative lg:w-5/12 text-center max-w-lg mx-auto lg:max-w-none lg:text-left">
        <h5>{t('title')}</h5>
        <p className="mt-md text-gray-default">{t('payment_gateways.stripe.description')}</p>
        <Checkbox checked={termsAccepted} className="mt-md" onChange={(e) => setTermsAccepted(e.target.checked)}>
          <Trans
            t={t}
            components={{
              FoodAmigosTerms: (
                <a href={serviceProvider.participation_agreement_url} target="_blank" rel="noreferrer noopener" />
              ),
              StripeTerms: (
                <a href="https://stripe.com/legal/connect-account" target="_blank" rel="noreferrer noopener" />
              ),
            }}
          >
            payment_gateways.stripe.form.terms_acceptance.label
          </Trans>
        </Checkbox>
        <Button type="primary" className="mt-md" loading={loading} disabled={!termsAccepted} onClick={onProceed}>
          {t('buttons.proceed')}
        </Button>
      </div>
      <div className="relative lg:mt-0 flex-1 flex flex-col justify-center items-center lg:items-end lg:p-md lg:self-end order-first lg:order-last">
        <ConfirmInformationIllustration />
      </div>
    </div>
  );
}
